<template>
  <div class="mobile-container">
    <div class="search">
      <input
        type="text"
        placeholder="CIQ ID or Ticker"
        v-model="query"
        @keyup.enter="querySubmitted"
      />
      <button class="icon-btn" @click="querySubmitted" title="Search">⚲</button>
    </div>

    <div class="date-picker-wrapper">
      <VueDatePicker
        v-model="endDate"
        :format="pickerFormat"
        @update:model-value="pickerUpdated"
        position="left"
      />
    </div>

    <div v-if="loading" class="loader">Loading chart data...</div>
    <div v-if="error" class="error">{{ error }}</div>

    <div class="chart-card" v-if="chartLoaded">
      <div class="chart-title">{{ chartTitle }}</div>
      <div class="chart-body">
        <highcharts
          :constructor-type="'stockChart'"
          :options="chartOptions"
          ref="highchartRef"
        ></highcharts>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/dashboard4';
import { ref } from 'vue';
let moment = require('moment');

export default {
  name: 'Dashboard4Mobile',
  setup: () => {
    const highchartRef = ref(null);
    const pickerFormat = (d) => {
      let e = moment(d).format('MM/DD/YYYY');
      return 'End: ' + e + ' (2yr history)';
    };
    return {
      highchartRef,
      pickerFormat,
    };
  },

  data: () => {
    return {
      query: 'IQ24937',
      ticker: '',
      name: '',
      endDate: new Date(),
      chartTitle: '',
      loading: false,
      error: null,
      chartLoaded: false,
      chartOptions: {
        title: {
          text: '',
          style: {
            fontSize: '14px',
          },
        },

        xAxis: {
          type: 'datetime',
          labels: {
            style: { fontSize: '10px' },
          },
        },

        yAxis: [
          {
            height: '60%',
            labels: {
              style: { fontSize: '10px' },
            },
          },
          {
            top: '65%',
            height: '35%',
            offset: 0,
            lineWidth: 2,
            labels: {
              style: { fontSize: '10px' },
            },
          },
        ],

        rangeSelector: {
          inputEnabled: false,
          buttonTheme: {
            width: 40,
            style: {
              fontSize: '9px',
            },
          },
          labelStyle: {
            fontSize: '10px',
          },
        },

        navigator: {
          height: 30,
          xAxis: {
            labels: {
              style: { fontSize: '9px' },
            },
          },
        },

        chart: {
          height: 500,
        },

        legend: {
          enabled: false,
        },

        tooltip: {
          style: {
            fontSize: '11px',
          },
        },

        series: [
          {
            name: '',
            type: 'hollowcandlestick',
            data: [],
            tooltip: {
              valueDecimals: 2,
            },
            id: 'price',
          },
          {
            type: 'column',
            name: 'Volume',
            data: [],
            yAxis: 1,
          },
          {
            type: 'flags',
            onSeries: 'price',
            shape: 'squarepin',
            data: [],
            width: 8,
            style: {
              fontSize: '9px',
            },
          },
        ],
      },
    };
  },

  mounted: async function () {
    await this.refresh();
  },

  methods: {
    refresh: async function () {
      this.loading = true;
      this.error = null;
      this.chartLoaded = false;

      let loader = this.$loading.show({
        height: 64,
        width: 64,
        color: '#00ab00',
        backgroundColor: '#4b4b4b',
        isFullPage: true,
        opacity: 0.5,
      });

      try {
        const labels = await api.fetchChartLabels(this.query);
        if (labels.errMsg != '') {
          this.error = this.query + ' is not valid identifier: ' + labels.errMsg;
          return;
        }

        this.chartTitle = labels.name + ' (' + labels.ticker + ')';
        this.chartOptions.title.text = '';
        this.chartOptions.series[0].name = labels.ticker;

        let s = moment(this.endDate).subtract(2, 'years').format('MM/DD/YYYY');
        let e = moment(this.endDate).format('MM/DD/YYYY');
        let ohlc = await api.fetchChartPrices(this.query, s, e);
        this.chartOptions.series[0].data = ohlc;

        let r = await api.fetchVolumesAndKeyDevs(this.query, s, e);
        let volumes = r.volumes;
        this.chartOptions.series[1].data = volumes;

        let k = r.keydevs;
        let keydevs = [];
        k.forEach((e) => {
          const d = new Date(e.date);
          const exists = keydevs.find((ee) => ee.x.getTime() == d.getTime());
          if (!exists) {
            keydevs.push({ x: d, title: ' ', text: e.headline });
          } else {
            exists.text += '<br>' + e.headline;
          }
        });
        this.chartOptions.series[2].data = keydevs;

        this.chartLoaded = true;

        // Set initial zoom range
        this.$nextTick(() => {
          if (this.highchartRef && this.highchartRef.chart) {
            const xAxis = this.highchartRef.chart.xAxis[0];
            s = ohlc[ohlc.length - 60][0];
            e = ohlc[ohlc.length - 1][0];
            xAxis.setExtremes(s, e);
          }
        });
      } catch (e) {
        console.error(e);
        this.error = e.message || String(e);
      } finally {
        this.loading = false;
        loader.hide();
      }
    },

    pickerUpdated: async function () {
      await this.refresh();
    },

    querySubmitted: async function () {
      await this.refresh();
    },
  },
};
</script>

<style scoped>
.mobile-container {
  padding: 8px;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 13px;
}

.search {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
}

.search input[type="text"] {
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
}

.search .icon-btn {
  width: 36px;
  height: 36px;
  padding: 0;
  font-size: 18px;
  font-weight: 600;
  border: none;
  background: #007a3f;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search .icon-btn:active {
  background: #005a2f;
}

.date-picker-wrapper {
  margin-bottom: 12px;
}

.loader {
  text-align: center;
  color: #666;
  padding: 20px;
  font-size: 13px;
}

.error {
  color: #b00;
  padding: 10px;
  background: #fee;
  border-radius: 4px;
  margin-bottom: 10px;
  font-size: 13px;
}

.chart-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 12px;
  overflow: hidden;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  padding: 10px 12px;
  background: #d6dce4;
}

.chart-body {
  padding: 4px;
}

/* Ensure Highcharts responsive on mobile */
:deep(.highcharts-container) {
  width: 100% !important;
}

:deep(.highcharts-root) {
  width: 100%;
}
</style>